import type { ComponentType, PropsWithChildren } from 'react';
import type React from 'react';

import { TrackingContext } from '@change-corgi/core-react/tracking';

import type { ErrorStateDetails } from '#src/app/shared/utils/async';
import { isError, isLoaded } from '#src/app/shared/utils/async';

import type { SocialStarterCombinedState, SocialStarterPrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { SocialStarterPageDataContextProvider } from './pageData';

export type ErrorHandlerProps = { context: ErrorStateDetails<SocialStarterCombinedState> };

type Props = {
	prefetchedData: SocialStarterPrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export const SocialStarterPageContextProvider: React.FC<PropsWithChildren<Props>> = ({
	children,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}) => {
	const pageContextState = usePageContext(prefetchedData);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData } = pageContextState;

	return (
		<SocialStarterPageDataContextProvider pageData={pageData}>
			<TrackingContext properties={{ user_handle: pageData.data.username ?? undefined }}>{children}</TrackingContext>
		</SocialStarterPageDataContextProvider>
	);
};
